import React, { useState } from 'react';
import {CreateConsentResponse} from '../blinkpay';
import {createConsent} from "../demoBffClientInstance";

interface RedirectFlowProps {
    onSubmit?: (payload: any) => void;
    onClose?: () => void;
}

const RedirectFlow: React.FC<RedirectFlowProps> = ({ onSubmit, onClose }) => {
    const isProd = process.env.REACT_APP_ENV === 'prod';
    const [bank, setBank] = useState<string>(isProd ? 'ASB' : 'PNZ');
    const [redirectUri, setRedirectUri] = useState<string>(process.env.REACT_APP_BLINKPAY_REDIRECT_URI || 'http://localhost:3000/redirect');
    const [permissions, setPermissions] = useState({
        ReadAccounts: true,
        ReadBalances: true,
        ReadTransactions: true,
        ReadStatements: true,
        ReadParty: true
    });
    const [expirationDateTime, setExpirationDateTime] = useState<string>('');
    const [startDateTime, setStartDateTime] = useState<string>('');
    const [endDateTime, setEndDateTime] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const banks = [
        ...(isProd ? [] : [{ id: 'PNZ', name: 'PNZ' }]),
        { id: 'ANZ', name: 'ANZ' },
        { id: 'ASB', name: 'ASB' },
        { id: 'BNZ', name: 'BNZ' },
        { id: 'Westpac', name: 'Westpac' }
    ];

    const handlePermissionChange = (permission: string) => {
        setPermissions(prev => ({
            ...prev,
            [permission]: !prev[permission as keyof typeof prev]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const selectedPermissions = Object.entries(permissions)
            .filter(([_, selected]) => selected)
            .map(([permission]) => permission);

        const payload: any = {
            flow: {
                detail: {
                    type: 'redirect',
                    bank: bank,
                    redirect_uri: redirectUri
                }
            },
            permissions: selectedPermissions
        };

        if (expirationDateTime) {
            payload.expiration_date_time = new Date(expirationDateTime).toISOString();
        }
        if (startDateTime) {
            payload.start_date_time = new Date(startDateTime).toISOString();
        }
        if (endDateTime) {
            payload.end_date_time = new Date(endDateTime).toISOString();
        }

        console.log('Redirect Flow Payload:', payload);

        try {
            // Call the Blink API to create consent
            console.log('Creating consent...');

            const response: CreateConsentResponse = await createConsent(payload);
            console.log('Consent created successfully:', response);

            if (response?.consentId) {
                console.log('Consent ID:', response.consentId);

                // Store consent ID in localStorage for demo purposes
                localStorage.setItem('consentId', response.consentId);

                // If there's a redirect URL in the response, redirect to it
                if (response.redirectUri) {
                    console.log('Redirecting to:', response.redirectUri);
                    window.location.href = response.redirectUri;
                } else {
                    // If no redirect URI, navigate to redirect page with consent ID
                    window.location.href = `/redirect?cid=${response.consentId}`;
                }
            } else {
                setError('No consent ID received from server');
            }

            if (onSubmit) {
                onSubmit(response);
            }
        } catch (error: any) {
            console.error('Error creating consent:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create consent';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="redirect-flow-modal">
            <div className="redirect-flow-container">
                <div className="redirect-flow-header">
                    <h2>Redirect Flow Configuration</h2>
                    {onClose && (
                        <button className="close-btn" onClick={onClose}>×</button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="redirect-flow-form">
                    <div className="form-section">
                        <h3>Bank Selection</h3>
                        <div className="bank-selection">
                            {banks.map((b) => (
                                <label key={b.id} className={`bank-option ${bank === b.id ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="bank"
                                        value={b.id}
                                        checked={bank === b.id}
                                        onChange={(e) => setBank(e.target.value)}
                                    />
                                    <span>{b.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Redirect Configuration</h3>
                        <div className="form-group">
                            <label htmlFor="redirect-uri">Redirect URI:</label>
                            <input
                                id="redirect-uri"
                                type="url"
                                value={redirectUri}
                                onChange={(e) => setRedirectUri(e.target.value)}
                                required
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Permissions</h3>
                        <div className="permissions-grid">
                            {Object.entries(permissions).map(([permission, checked]) => (
                                <label key={permission} className="permission-option">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handlePermissionChange(permission)}
                                    />
                                    <span>{permission.replace(/([A-Z])/g, ' $1').trim()}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Date/Time Configuration (Optional)</h3>
                        <div className="datetime-grid">
                            <div className="form-group">
                                <label htmlFor="start-datetime">Start Date/Time:</label>
                                <input
                                    id="start-datetime"
                                    type="datetime-local"
                                    value={startDateTime}
                                    onChange={(e) => setStartDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="end-datetime">End Date/Time:</label>
                                <input
                                    id="end-datetime"
                                    type="datetime-local"
                                    value={endDateTime}
                                    onChange={(e) => setEndDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="expiration-datetime">Expiration Date/Time (Null means indefinite):</label>
                                <input
                                        id="expiration-datetime"
                                        type="datetime-local"
                                        value={expirationDateTime}
                                        onChange={(e) => setExpirationDateTime(e.target.value)}
                                        className="form-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Creating Consent...' : 'Initiate Redirect Flow'}
                        </button>
                        {onClose && (
                            <button type="button" onClick={onClose} className="cancel-btn" disabled={loading}>
                                Cancel
                            </button>
                        )}
                    </div>

                    {error && (
                            <div className="error-message">
                                {error}
                            </div>
                    )}
                </form>
            </div>

            <style>{`
                .redirect-flow-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    overflow-y: auto;
                }

                .redirect-flow-container {
                    background: white;
                    border-radius: 20px;
                    max-width: 800px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .redirect-flow-header {
                    padding: 30px;
                    border-bottom: 2px solid #f0f0f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .redirect-flow-header h2 {
                    margin: 0;
                    color: #333;
                    font-size: 24px;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 30px;
                    color: #999;
                    cursor: pointer;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: all 0.3s ease;
                }

                .close-btn:hover {
                    background: #f0f0f0;
                    color: #333;
                }

                .redirect-flow-form {
                    padding: 30px;
                }

                .form-section {
                    margin-bottom: 30px;
                }

                .form-section h3 {
                    color: #555;
                    font-size: 18px;
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .bank-selection {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                    gap: 10px;
                }

                .bank-option {
                    display: flex;
                    align-items: center;
                    padding: 12px;
                    border: 2px solid #e0e0e0;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: white;
                }

                .bank-option:hover {
                    border-color: #667eea;
                    background: #f8f9fa;
                }

                .bank-option.selected {
                    border-color: #667eea;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                }

                .bank-option input[type="radio"] {
                    margin-right: 8px;
                    accent-color: #667eea;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    color: #555;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .form-input {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 16px;
                    background: #f8f9fa;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #667eea;
                    background: white;
                }

                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                }

                .permission-option {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .permission-option:hover {
                    background: #f8f9fa;
                    border-color: #667eea;
                }

                .permission-option input[type="checkbox"] {
                    margin-right: 10px;
                    accent-color: #667eea;
                    width: 18px;
                    height: 18px;
                }

                .datetime-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    padding-top: 20px;
                    border-top: 1px solid #e0e0e0;
                }

                .submit-btn {
                    padding: 15px 30px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
                }

                .submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .cancel-btn {
                    padding: 15px 30px;
                    background: #f0f0f0;
                    color: #666;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .cancel-btn:hover {
                    background: #e0e0e0;
                    color: #333;
                }

                @media (max-width: 600px) {
                    .redirect-flow-modal {
                        padding: 0;
                    }

                    .redirect-flow-container {
                        border-radius: 0;
                        max-height: 100vh;
                    }

                    .bank-selection {
                        grid-template-columns: 1fr;
                    }

                    .permissions-grid {
                        grid-template-columns: 1fr;
                    }

                    .datetime-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .submit-btn,
                    .cancel-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default RedirectFlow;